import { useState } from 'react';
import { SIZE_SPECS } from '@hearth/shared';
import {
  BREAK_EVEN_HOURS, CHART, DEFAULT_HOURS, DEFAULT_MONTHS, estimate, FLAT_PER_MONTH, layout, money, MONTH_NAMES, SETUP_FEE,
} from './costs';

/** What a group pays over a year, against a typical flat-rate server: the hours and the months are the visitor's to move. */
export function CostCalculator() {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [played, setPlayed] = useState<readonly boolean[]>(DEFAULT_MONTHS);

  const e = estimate(hours, played);
  const chart = layout(e, played);
  const count = played.filter(Boolean).length;
  const cheaper = e.saved >= 0;
  const toggle = (i: number) => setPlayed(played.map((on, j) => (j === i ? !on : on)));

  return (
    <section className="calc" aria-labelledby="calc-title">
      <div className="calc-head">
        <h3 id="calc-title">Estimate running cost over the course of a year</h3>
      </div>

      <div className="calc-controls">
        <div className="calc-field">
          <label htmlFor="calc-hours">How much you play</label>
          <input id="calc-hours" type="range" min={1} max={40} step={1} value={hours} onChange={(ev) => setHours(Number(ev.target.value))} />
          <small>
            <b>{`${hours} ${hours === 1 ? 'hour' : 'hours'} a week`}</b>
            <span>, about {Math.round(e.monthHours)} hours a month: {money(e.monthCost)} in a month you play.</span>
          </small>
        </div>
        <div className="calc-field">
          <span id="calc-months">Months you play</span>
          <div className="chips" role="group" aria-labelledby="calc-months">
            {MONTH_NAMES.map((name, i) => (
              <button key={name} type="button" className="chip" aria-pressed={played[i]} onClick={() => toggle(i)}>{name}</button>
            ))}
          </div>
          {count === 0 && <small>Pick the months your group plays.</small>}
        </div>
      </div>

      <ul className="calc-legend">
        <li><i className="sw sw-hearth" />Hearth Small ({SIZE_SPECS.small.players})</li>
        <li><i className="sw sw-flat" />Similar hosted server (4 GB), ${FLAT_PER_MONTH} a month</li>
      </ul>

      <div className="calc-scroll">
        <svg
          className="calc-chart"
          viewBox={`0 0 ${CHART.width} ${CHART.height}`}
          role="img"
          aria-label={`Total paid by the end of each month. A typical hosted server reaches $${e.flatTotal} after twelve months. Hearth reaches ${money(e.hearthTotal)}, including the ${money(SETUP_FEE)} setup.`}
        >
          {chart.bands.map((b) => (
            <rect key={b.x} className="band-played" x={b.x} y={CHART.top} width={b.w} height={CHART.height - CHART.top - CHART.bottom} />
          ))}
          {chart.grid.map((g) => (
            <g key={g.label}>
              <line className="grid" x1={CHART.left} x2={CHART.width - CHART.right} y1={g.y} y2={g.y} />
              <text className="tick tick-y" x={CHART.left - 10} y={g.y + 4}>{g.label}</text>
            </g>
          ))}
          {chart.months.map((m) => (
            <text key={m.name} className="tick tick-x" x={m.x} y={m.y}>{m.name}</text>
          ))}
          <text className="axis" x={chart.axes.x.x} y={chart.axes.x.y}>Month</text>
          <text className="axis" x={chart.axes.y.x} y={chart.axes.y.y} transform={`rotate(-90 ${chart.axes.y.x} ${chart.axes.y.y})`}>Total Paid ($)</text>
          <polyline className="line-flat" points={chart.flatPoints} />
          <polyline className="line-hearth" points={chart.hearthPoints} />
          <circle className="dot-flat" cx={chart.flatEnd.x} cy={chart.flatEnd.y} r={5} />
          <circle className="dot-hearth" cx={chart.hearthEnd.x} cy={chart.hearthEnd.y} r={5} />
          <text className="end end-flat" x={chart.flatEnd.x + 12} y={chart.flatEnd.labelY}>{chart.flatEnd.label}</text>
          <text className="end end-hearth" x={chart.hearthEnd.x + 12} y={chart.hearthEnd.labelY}>{chart.hearthEnd.label}</text>
        </svg>
      </div>

      <div className="calc-totals">
        <div><span>Typical hosted server, a year</span><strong>${e.flatTotal}</strong></div>
        <div className="calc-hearth"><span>Hearth, a year</span><strong>{money(e.hearthTotal)}</strong><small>Includes the {money(SETUP_FEE)} one-time setup</small></div>
        <div className={cheaper ? 'calc-saved' : undefined}><span>{cheaper ? 'Left in your pocket' : 'A flat plan costs less by'}</span><strong>{money(Math.abs(e.saved))}</strong></div>
      </div>

      <p className="calc-note">Play more than {BREAK_EVEN_HOURS} hours every month of the year and a flat plan costs less.</p>
    </section>
  );
}
